// src/utils/taxYear.js — frontend UK fiscal year helpers

export function normalizeTaxCountry(country) {
  const c = String(country || 'DE').trim().toUpperCase().replace(/\s+/g, '_');
  if (['UK', 'GB', 'UNITED_KINGDOM', 'UNITED KINGDOM'].includes(c)) return 'UK';
  return c;
}

export function toUnixSecondsLoose(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return n > 1e12 ? Math.floor(n / 1000) : Math.floor(n);
}

export function getUkTaxYearFromTimestamp(tsInput) {
  const ts = toUnixSecondsLoose(tsInput);
  if (!ts) return null;
  const d = new Date(ts * 1000);
  const y = d.getUTCFullYear();
  const startCurrent = Date.UTC(y, 3, 6, 0, 0, 0);
  return d.getTime() >= startCurrent ? y : y - 1;
}

export function rowMatchesTaxYear(row = {}, selectedCountry = 'DE', taxYear) {
  const yearInt = Number(taxYear);
  if (!Number.isFinite(yearInt) || yearInt <= 0) return true;

  const ts = toUnixSecondsLoose(
    row?.timestamp ?? row?.ts ?? row?.block_timestamp ?? row?.date ?? 0
  );
  if (!ts) return false;

  if (normalizeTaxCountry(selectedCountry) === 'UK') {
    return getUkTaxYearFromTimestamp(ts) === yearInt;
  }

  return new Date(ts * 1000).getUTCFullYear() === yearInt;
}

export function formatUkTaxYearLabel(year) {
  const y = Number(year);
  if (!Number.isFinite(y) || y <= 0) return String(year || '');
  return `${y}/${String((y + 1) % 100).padStart(2, '0')}`;
}

export function isUkTaxCountry(country = '') {
  return normalizeTaxCountry(country) === 'UK';
}

/** Sichtbares Steuerjahr-Label (UK: 2025/26, sonst: 2025). Berechnungs-Key bleibt numerisch. */
export function formatTaxYearLabel(year, country = 'DE') {
  const y = Number(year);
  if (!Number.isFinite(y) || y <= 0) return String(year || '');

  if (isUkTaxCountry(country)) {
    return formatUkTaxYearLabel(y);
  }

  return String(y);
}

/** UK Langform z. B. 2025/2026 — optional für explizit lange Labels. */
export function formatTaxYearLabelLong(year, country = 'DE') {
  const y = Number(year);
  if (!Number.isFinite(y) || y <= 0) return String(year || '');

  if (isUkTaxCountry(country)) {
    return `${y}/${y + 1}`;
  }

  return String(y);
}

/** Print/PDF: explizites displayTaxYear hat Vorrang, sonst numerisches year. */
export function resolveDisplayTaxYearLabel(year, country = 'DE', displayTaxYear) {
  if (displayTaxYear != null && String(displayTaxYear).trim() !== '') {
    return String(displayTaxYear);
  }
  return formatTaxYearLabel(year, country);
}

export function getTaxYearStartTs(selectedCountry = 'DE', taxYear) {
  const yearInt = Number(taxYear);
  if (!Number.isFinite(yearInt) || yearInt <= 0) return null;

  if (normalizeTaxCountry(selectedCountry) === 'UK') {
    return Math.floor(Date.UTC(yearInt, 3, 6, 0, 0, 0) / 1000);
  }

  return Math.floor(Date.UTC(yearInt, 0, 1, 0, 0, 0) / 1000);
}

export function getTaxYearEndExclusiveTs(selectedCountry = 'DE', taxYear) {
  const yearInt = Number(taxYear);
  if (!Number.isFinite(yearInt) || yearInt <= 0) return null;

  if (normalizeTaxCountry(selectedCountry) === 'UK') {
    return Math.floor(Date.UTC(yearInt + 1, 3, 6, 0, 0, 0) / 1000);
  }

  return Math.floor(Date.UTC(yearInt + 1, 0, 1, 0, 0, 0) / 1000);
}

/** UK 30-day bed-and-breakfast matching horizon after tax-year end. */
export const UK_MATCHING_LOOKAHEAD_DAYS = 30;

/**
 * Exclusive end of data needed for tax *calculation*.
 * For UK this is report end + 30 days. Report rows / year-end balance stay at tax-year end.
 */
export function getTaxCalculationEndExclusiveTs(
  selectedCountry = 'DE',
  taxYear
) {
  const reportEndExclusiveTs =
    getTaxYearEndExclusiveTs(selectedCountry, taxYear);

  if (reportEndExclusiveTs == null) {
    return null;
  }

  if (normalizeTaxCountry(selectedCountry) === 'UK') {
    return (
      reportEndExclusiveTs +
      UK_MATCHING_LOOKAHEAD_DAYS * 24 * 60 * 60
    );
  }

  return reportEndExclusiveTs;
}

export function getTaxYearLabel(selectedCountry = 'DE', taxYear) {
  return formatTaxYearLabel(taxYear, selectedCountry);
}

export function getTaxYearPeriod(selectedCountry = 'DE', taxYear) {
  const startTs = getTaxYearStartTs(selectedCountry, taxYear);
  const endExclusiveTs = getTaxYearEndExclusiveTs(selectedCountry, taxYear);
  if (startTs == null || endExclusiveTs == null) return null;

  return {
    startTs,
    endExclusiveTs,
    endInclusiveTs: endExclusiveTs - 1,
    label: getTaxYearLabel(selectedCountry, taxYear),
  };
}

export function isTaxYearOpen(
  selectedCountry = 'DE',
  taxYear,
  asOfTs = Math.floor(Date.now() / 1000)
) {
  const period = getTaxYearPeriod(selectedCountry, taxYear);
  if (!period) return false;
  const ts = toUnixSecondsLoose(asOfTs);
  return ts >= period.startTs && ts < period.endExclusiveTs;
}
